import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal, ModalContent, Spinner } from '@nextui-org/react';
import { ChevronRight, MapPin, Navigation, Search, SearchX, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import searchService, {
  GroupedSearchResults,
  SearchResult,
  SearchResultType,
} from '../services/searchService';
import nearbyService, {
  formatDistance,
  GeoPoint,
  NearbyItem,
} from '../services/nearbyService';
import userService from '../services/userService';
import { useAuth } from './AuthContext';

// ============================================
// SearchExplorer – Port des Flutter SearchExplorer Modals
//
// Schnellsuche von überall in der App. Öffnet als zentriertes Modal.
// Wird via useSearchExplorer().open() geöffnet (Header-Suchicon, Hero,
// sowie global per ⌘K / Strg+K).
// ============================================

const ACCENT = '#3fced1';
const DEBOUNCE_MS = 300;

// Reihenfolge & Emoji-Labels exakt wie im Flutter SearchExplorer
const EXPLORER_ORDER: SearchResultType[] = [
  'tournament',
  'series',
  'club',
  'pub',
  'shop',
  'news',
  'forum',
];

const EXPLORER_LABELS: Record<SearchResultType, string> = {
  tournament: '🏆 Turniere',
  series: '📊 Turnierserien',
  club: '👥 Vereine',
  pub: '📍 Spielstätten',
  shop: '🛒 Dart-Shops',
  news: '📰 News',
  forum: '💬 Forum',
};

interface SearchExplorerContextValue {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const SearchExplorerContext = createContext<SearchExplorerContextValue | null>(null);

export const useSearchExplorer = (): SearchExplorerContextValue => {
  const ctx = useContext(SearchExplorerContext);
  if (!ctx) {
    throw new Error('useSearchExplorer muss innerhalb des SearchExplorerProvider verwendet werden.');
  }
  return ctx;
};

export const SearchExplorerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Globaler Shortcut ⌘K / Strg+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <SearchExplorerContext.Provider value={value}>
      {children}
      <SearchExplorerModal isOpen={isOpen} onClose={close} />
    </SearchExplorerContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const SearchExplorerModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<GroupedSearchResults>(searchService.emptyResults());
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ---------- "In deiner Nähe" (wie NearbyPage: Profil-Adresse > Browser-Standort) ----------
  const profileLocation = useMemo<GeoPoint | null>(
    () => nearbyService.getProfileLocation(user),
    [user],
  );
  const [browserLocation, setBrowserLocation] = useState<GeoPoint | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const nearbyLocation = browserLocation ?? profileLocation;

  const [nearbyItems, setNearbyItems] = useState<NearbyItem[] | null>(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const nearbyLocKeyRef = useRef<string | null>(null);

  // "In der Nähe von <Suchbegriff>" – Geocoding des Suchbegriffs (wie Flutter-Turniersuche)
  const [queryNearby, setQueryNearby] = useState<{ label: string; items: NearbyItem[] } | null>(
    null,
  );
  const [queryNearbyLoading, setQueryNearbyLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !nearbyLocation) return;
    const key = `${nearbyLocation.latitude},${nearbyLocation.longitude}`;
    if (nearbyLocKeyRef.current === key) return;
    nearbyLocKeyRef.current = key;
    let stale = false;
    setNearbyLoading(true);
    nearbyService
      .loadNearbyItems(nearbyLocation, { radiusKm: 100, limit: 8 })
      .then((items) => {
        if (!stale) setNearbyItems(items);
      })
      .catch(() => {
        if (!stale) setNearbyItems([]);
      })
      .finally(() => {
        if (!stale) setNearbyLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [isOpen, nearbyLocation]);

  const requestBrowserLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Standortbestimmung wird von diesem Browser nicht unterstützt.');
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLoading(false);
        setBrowserLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          label: 'Mein Standort',
        });
      },
      () => {
        setGeoLoading(false);
        setGeoError('Standort konnte nicht ermittelt werden.');
      },
      { timeout: 10000 },
    );
  };

  // Beim Öffnen zurücksetzen und fokussieren
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setDebounced('');
      setResults(searchService.emptyResults());
      setHasSearched(false);
      setLoading(false);
      const t = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);

  // Debounce
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  // Suche ausführen
  useEffect(() => {
    if (!isOpen) return;
    if (debounced.length < searchService.minQueryLength) {
      requestIdRef.current += 1;
      setLoading(false);
      setResults(searchService.emptyResults());
      setHasSearched(false);
      setQueryNearby(null);
      setQueryNearbyLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    searchService
      .searchAll(debounced)
      .then((grouped) => {
        if (requestId !== requestIdRef.current) return;
        setResults(grouped);
        setHasSearched(true);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setResults(searchService.emptyResults());
        setHasSearched(true);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });

    // Parallel: Suchbegriff geocoden (z.B. "Melle") und Umkreis-Ergebnisse laden
    setQueryNearby(null);
    setQueryNearbyLoading(true);
    userService
      .geocodeAddress(debounced)
      .then(async (geos) => {
        if (requestId !== requestIdRef.current) return;
        const geo = Array.isArray(geos)
          ? geos.find(
              (g) =>
                typeof g?.latitude === 'number' &&
                isFinite(g.latitude) &&
                typeof g?.longitude === 'number' &&
                isFinite(g.longitude),
            )
          : undefined;
        if (!geo) return;
        // Mehr Kandidaten laden als angezeigt werden: die nächstgelegenen sind oft
        // identisch mit den normalen Suchtreffern und fallen der Deduplizierung zum Opfer
        const items = await nearbyService.loadNearbyItems(
          { latitude: geo.latitude as number, longitude: geo.longitude as number },
          { radiusKm: 50, limit: 24 },
        );
        if (requestId !== requestIdRef.current) return;
        setQueryNearby({
          label:
            typeof geo.formattedAddress === 'string' && geo.formattedAddress.trim()
              ? geo.formattedAddress.trim()
              : debounced,
          items,
        });
      })
      .catch(() => {
        /* Geocoding-Fehler still ignorieren – normale Suche bleibt unberührt */
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setQueryNearbyLoading(false);
      });
  }, [debounced, isOpen]);

  const totalCount = useMemo(() => searchService.countResults(results), [results]);

  // Umkreis-Treffer, die bereits in den normalen Ergebnissen stehen, ausblenden
  const queryNearbyItems = useMemo<NearbyItem[]>(() => {
    if (!queryNearby) return [];
    const knownLinks = new Set<string>();
    (Object.values(results) as SearchResult[][]).forEach((list) => {
      (list || []).forEach((r) => {
        if (r?.link) knownLinks.add(r.link);
      });
    });
    return queryNearby.items
      .filter((item) => !item.link || !knownLinks.has(item.link))
      .slice(0, 6);
  }, [queryNearby, results]);

  const handleResultClick = (result: SearchResult) => {
    if (result.link) {
      onClose();
      navigate(result.link);
    }
  };

  // Mobile: Vollbild-Explorer statt schwebendem Modal
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
      size={isMobileViewport ? 'full' : '2xl'}
      scrollBehavior="inside"
      backdrop="blur"
      placement={isMobileViewport ? 'center' : 'top'}
      classNames={{
        base: isMobileViewport
          ? 'bg-zinc-900 m-0 rounded-none h-[100dvh] max-h-[100dvh]'
          : 'bg-zinc-900 border border-zinc-800 mt-16',
        backdrop: 'bg-black/60',
      }}
    >
      <ModalContent>
        {(onCloseInner) => (
          <>
            {/* Header mit Suchfeld */}
            <div className="flex items-center gap-3 p-4 sm:p-5">
              <Search size={22} className="shrink-0 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="News, Turniere, Vereine, Spielstätten suchen…"
                className="flex-1 bg-transparent text-base text-white placeholder:text-gray-500 outline-none border-none"
              />
              <button
                type="button"
                aria-label="Suche schließen"
                onClick={onCloseInner}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-gray-300 hover:bg-zinc-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="h-px bg-zinc-800" />

            {/* Ergebnisse */}
            <div
              className={`${
                isMobileViewport ? 'flex-1' : 'max-h-[60vh]'
              } overflow-y-auto p-4 sm:p-5`}
            >
              {/* Leerer Zustand: "In deiner Nähe" wie auf /in-der-naehe */}
              {!loading && debounced.length < searchService.minQueryLength && (
                <div>
                  {nearbyLocation ? (
                    <section>
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <h3 className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-gray-400">
                          <MapPin size={15} style={{ color: ACCENT }} className="shrink-0" />
                          <span className="shrink-0">In deiner Nähe</span>
                          {nearbyLocation.label && (
                            <span className="truncate text-xs font-normal text-gray-500">
                              · {nearbyLocation.label}
                            </span>
                          )}
                        </h3>
                        <button
                          type="button"
                          onClick={() => {
                            onCloseInner();
                            navigate('/in-der-naehe');
                          }}
                          className="flex shrink-0 items-center gap-0.5 text-xs font-semibold transition-opacity hover:opacity-80"
                          style={{ color: ACCENT }}
                        >
                          Alle anzeigen
                          <ChevronRight size={14} />
                        </button>
                      </div>

                      {nearbyLoading && (
                        <div className="flex items-center justify-center py-10">
                          <Spinner color="primary" size="sm" />
                        </div>
                      )}

                      {!nearbyLoading && nearbyItems && nearbyItems.length > 0 && (
                        <div className="space-y-2">
                          {nearbyItems.map((item) => (
                            <NearbyResultRow
                              key={`${item.type}-${item.id}`}
                              item={item}
                              onClick={() => {
                                if (item.link) {
                                  onCloseInner();
                                  navigate(item.link);
                                }
                              }}
                            />
                          ))}
                        </div>
                      )}

                      {!nearbyLoading && nearbyItems && nearbyItems.length === 0 && (
                        <p className="py-6 text-center text-sm text-gray-500">
                          Keine Einträge in deiner Nähe gefunden.
                        </p>
                      )}
                    </section>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Search size={56} className="mb-4 text-zinc-700" />
                      <p className="max-w-xs text-base text-gray-500">
                        Suche nach News, Turnieren, Vereinen, Spielstätten…
                      </p>
                      <button
                        type="button"
                        onClick={requestBrowserLocation}
                        disabled={geoLoading}
                        className="mt-6 inline-flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-800/60 px-4 py-2.5 text-sm font-semibold text-gray-200 transition-colors hover:bg-zinc-800 disabled:opacity-60"
                      >
                        {geoLoading ? (
                          <Spinner size="sm" color="primary" />
                        ) : (
                          <Navigation size={15} style={{ color: ACCENT }} />
                        )}
                        Ergebnisse in meiner Nähe anzeigen
                      </button>
                      {geoError && (
                        <p className="mt-2 text-xs text-red-400">{geoError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Laden */}
              {loading && (
                <div className="flex items-center justify-center py-16">
                  <Spinner color="primary" />
                </div>
              )}

              {/* Keine Treffer (weder Suche noch Umkreis) */}
              {!loading &&
                hasSearched &&
                totalCount === 0 &&
                !queryNearbyLoading &&
                queryNearbyItems.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <SearchX size={64} className="mb-4 text-zinc-700" />
                    <p className="text-base text-gray-500">Keine Ergebnisse für „{debounced}"</p>
                  </div>
                )}

              {/* Gruppierte Treffer + Umkreis-Sektion */}
              {!loading && hasSearched && (totalCount > 0 || queryNearbyItems.length > 0) && (
                <div className="space-y-6">
                  {EXPLORER_ORDER.map((type) => {
                    const items = results[type] || [];
                    if (items.length === 0) return null;
                    return (
                      <section key={type}>
                        <h3 className="mb-3 text-sm font-semibold text-gray-400">
                          {EXPLORER_LABELS[type]} ({items.length})
                        </h3>
                        <div className="space-y-2">
                          {items.map((result) => (
                            <SearchResultRow
                              key={`${result.type}-${result.id}`}
                              result={result}
                              onClick={() => handleResultClick(result)}
                            />
                          ))}
                        </div>
                      </section>
                    );
                  })}

                  {/* Umkreis-Ergebnisse zum geocodeten Suchbegriff (z.B. "Melle") */}
                  {queryNearbyItems.length > 0 && queryNearby && (
                    <section>
                      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-gray-400">
                        <MapPin size={15} style={{ color: ACCENT }} className="shrink-0" />
                        <span className="min-w-0 truncate">
                          In der Nähe von {queryNearby.label}
                        </span>
                      </h3>
                      <div className="space-y-2">
                        {queryNearbyItems.map((item) => (
                          <NearbyResultRow
                            key={`qn-${item.type}-${item.id}`}
                            item={item}
                            onClick={() => {
                              if (item.link) {
                                onCloseInner();
                                navigate(item.link);
                              }
                            }}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const NearbyResultRow: React.FC<{ item: NearbyItem; onClick: () => void }> = ({
  item,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = item.image && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!item.link}
      className="flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-800/40 p-3 text-left transition-colors hover:bg-zinc-800 disabled:cursor-default"
    >
      {showImage ? (
        <img
          src={item.image as string}
          alt={item.name || ''}
          onError={() => setImgError(true)}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-zinc-800"
          style={{ color: ACCENT }}
        >
          <MapPin size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-sm font-semibold text-white">{item.name || '–'}</p>
        <p className="line-clamp-1 text-xs text-gray-400">
          <span className="text-gray-500">{item.typeLabel}</span>
          {item.subtitle ? ` · ${item.subtitle}` : ''}
        </p>
      </div>
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums"
        style={{ backgroundColor: `${ACCENT}14`, color: ACCENT }}
      >
        {formatDistance(item.distance)}
      </span>
    </button>
  );
};

const SearchResultRow: React.FC<{ result: SearchResult; onClick: () => void }> = ({
  result,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = result.imageUrl && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!result.link}
      className="flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-800/40 p-3 text-left transition-colors hover:bg-zinc-800 disabled:cursor-default"
    >
      {showImage ? (
        <img
          src={result.imageUrl as string}
          alt={result.title}
          onError={() => setImgError(true)}
          className="h-12 w-12 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-zinc-800"
          style={{ color: ACCENT }}
        >
          <Search size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-sm font-semibold text-white">{result.title}</p>
        {result.subtitle && (
          <p className="line-clamp-1 text-xs text-gray-400">{result.subtitle}</p>
        )}
      </div>
    </button>
  );
};

export default SearchExplorerProvider;
